<script setup lang="ts">
import {inject, ref, watch} from 'vue';
import OrderAdjustmentForm, {AdjustmentType} from '../OrderAdjustmentForm';
import Decimal from 'decimal.js';
import {
  LegacyAdjustmentCategory,
  LegacyOrder,
  legacyOrderCommands,
  legacyOrderKey,
  useLocalization
} from '@hantera/portal-app';
import useBackend from '@/composition/useBackend';
import {v4 as uuidv4} from 'uuid';
import {DecimalInputValue} from "@hantera/design-system";

const props = defineProps<{
  type: AdjustmentType
  value: DecimalInputValue
}>();

const emit = defineEmits<{
  (e: 'update:value', value: DecimalInputValue): void
}>();

const showForm = ref(false);

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const backend = useBackend();
const t = useLocalization();
const category = ref<LegacyAdjustmentCategory>();
const value = ref<DecimalInputValue>({
  value: new Decimal(0),
  isPercentage: false
});


watch(value, () => emit('update:value', value.value));

function openForm() {
  showForm.value = true;
}
async function submit() {
  if (value.value.value.eq(0)) {
    cancel();
    return;
  }
  if (!category.value) {
    // OrderAdjustmentForm ensures category is set
    return;
  }

  const adjustmentId = uuidv4();
  
  if (!order.value.includeVat) {
    // Turns out that accounting for VAT here causes more problems than it solves as legacy backend will mix things up / Kristoffer, 2025-03-29
    //priceWithVat = value.value.mul(order.value.orderVat.div(100).add(1));
  }
  
  await backend.legacyOrders.buildCommandGroup(
    order.value.orderId,
    `Add {name} ${props.type === AdjustmentType.Fee ? 'fee' : 'discount'} of {value}`,
    {
      value: value.value.value + (value.value.isPercentage ? '%' : ''),
      name: category.value.name,
    }
  )
    .command(legacyOrderCommands.addOrUpdateAdjustment, {
      adjustmentId: adjustmentId,
      percentage: value.value.isPercentage ? value.value.value.div(100) : undefined,
      priceWithVat: value.value.isPercentage ? undefined : value.value.value,
    })
    // TODO: Add Description and it's UI (if users miss it - otherwise we should drop this for now, maybe some kind of notes will be needed in the future but "Description" is a bit misleading)
    // .command(upsertRmaAdjustmentField, {
    //   rmaId: props.data.rmaId,
    //   rmaAdjustmentId: adjustmentId,
    //   fieldKey: 'Description',
    //   fieldValue: '',
    // })
    .command(legacyOrderCommands.upsertAdjustmentField, {
      adjustmentId: adjustmentId,
      fieldKey: 'CategoryCode',
      fieldValue: category.value.code,
    })
    .command(legacyOrderCommands.upsertAdjustmentField, {
      adjustmentId: adjustmentId,
      fieldKey: 'CategoryDescription',
      fieldValue: category.value.name,
    })
    .command(legacyOrderCommands.addActivityLog, {
      message: `Created {name} ${props.type === AdjustmentType.Fee ? 'fee' : 'discount'} of {value}`,
      fields: {
        value: value.value.value + (value.value.isPercentage ? '%' : ''),
        name: category.value.name,
      }
    })
    .send();

  cancel();
  return;
}
function cancel() {
  value.value = {
    value: new Decimal(0),
    isPercentage: false
  };
  emit('update:value', value.value);
  category.value = undefined;
  showForm.value = false;
}
</script>

<template>
  <div class="order-add-adjustment">
    <button
      class="btn-editable"
      v-if="!showForm"
      @click="openForm"
    >{{ t(`orderView.summary.add${props.type === AdjustmentType.Discount ? 'Discount' : 'Fee'}`) }}</button>
    <OrderAdjustmentForm
      v-if="showForm"
      v-model:selected-category="category"
      v-model:value="value"
      :type="type"
      @submit="submit"
      @cancel="cancel"
    />
  </div>
</template>

<style>
.order-add-adjustment {
  margin-top: 5px;
}

</style>